import { translate } from '../../../common/i18n';
import { TrackJSError } from '../../view/logger';
import { doUntilDone, tradeOptionToProposal } from '../tools';
import { clearProposals, proposalsReady } from './state/actions';

export default Engine =>
    class Proposal extends Engine {
        makeProposals(tradeOption) {
            if (!this.isNewTradeOption(tradeOption)) {
                return;
            }
            // Generate a purchase reference when trade options are different from previous trade options.
            // This will ensure the bot doesn't mistakenly purchase the wrong proposal.
            this.regeneratePurchaseReference();
            this.tradeOption = tradeOption;
            this.proposalTemplates = tradeOptionToProposal(tradeOption, this.getPurchaseReference());
            this.renewProposalsOnPurchase();
        }

        selectProposal(contractType) {
            // console.log(this.dataVirtual, this.data);
            const { proposals } = this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing ? this.dataVirtual : this.data;

            if (proposals.length === 0) {
                throw Error(translate('Proposals are not ready'));
            }

            const toBuy = proposals.find(proposal => {
                if (
                    proposal.contractType === contractType &&
                    proposal.purchaseReference === this.getPurchaseReference()
                ) {
                    // Below happens when a user has had one of the proposals return
                    // with a ContractBuyValidationError. We allow the logic to continue
                    // to here cause the opposite proposal may still be valid. Only once
                    // they attempt to purchase the errored proposal we will intervene.
                    // if (proposal.error) {
                    //     const { error } = proposal.error.error;
                    //     const { code, message } = error;const stats = duringPurchaseManager.getContractStats(contractId);
                    //     // Returns:
                    //     {
                    //         duration,      // Contract duration in seconds
                    //         profitLoss,    // Current P/L
                    //         currentPrice,  // Current market price
                    //         highestPrice,  // Highest price seen
                    //         lowestPrice,   // Lowest price seen
                    //         priceChange,   // Price change percentage
                    //         tickCount      // Number of ticks received
                    //     }
                    //     console.log(code, message, error);
                    //     throw new TrackJSError(code, message, error);
                    // }

                    return proposal;
                }

                return false;
            });

            if (!toBuy) {
                throw new TrackJSError(
                    'CustomInvalidProposal',
                    translate('Selected proposal does not exist'),
                    this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing ? this.dataVirtual.proposals : this.data.proposals
                );
            }
            return {
                proposal: toBuy,
                currency: this.tradeOption.currency,
            };
        }
        renewProposalsOnPurchase() {
            // console.log('renew');
            this.unsubscribeProposals().then(() => this.requestProposals());
        }
        async getPayout(params, hasSecondBarrier = false) {
            const proposal = {
                proposal       : 1,
                amount         : 100,
                barrier        : params.barrier,
                basis          : 'stake',
                'contract_type': params.type,
                currency       : 'USD',
                duration       : params.duration,
                'duration_unit': params.durationUnit,
                symbol         : this.tradeOption.symbol,
            };

            if (hasSecondBarrier) {
                proposal.barrier2 = params.barrier2;
            }

            try {
                const { proposal: { payout } } = await this.api.getPriceProposalForContract(proposal);
                return payout;
            } catch (error) {
                console.error('Error fetching payout:', error);
                return 0;
            }
        }
        getPayoutWithBarrier(params) {
            return this.getPayout.call(this, params);
        }

        getPayoutWithBarriers(params) {
            return this.getPayout.call(this, params, true);
        }
        clearProposals() {
            this.data.proposals = [];
            this.data.proposalsLive = [];
            this.store.dispatch(clearProposals());
        }
        clearProposalsVirtual() {
            this.dataVirtual.proposals = [];
            this.dataVirtual.proposalsLive = [];
        }
        requestProposals() {
            Promise.all(
                this.proposalTemplates.map(proposal => {
                    if(this.virtualSettings.active && this.virtualSettings.valid) {
                        this.virtualApi.subscribeToPriceForContractProposal(proposal).catch(error => {
                            if (error && error.name === 'ContractBuyValidationError') {
                                this.dataVirtual.proposals.push({
                                    ...error.error.echo_req,
                                    ...error.error.echo_req.passthrough,
                                    error,
                                });
                                return null;
                            }
                            throw error;
                        });
                    }
                    return this.api.subscribeToPriceForContractProposal(proposal).catch(error => {
                        if (error && error.name === 'ContractBuyValidationError') {
                            this.data.proposals.push({
                                ...error.error.echo_req,
                                ...error.error.echo_req.passthrough,
                                error,
                            });
                            return null;
                        }
                        throw error;
                    });
                })
            ).catch(e => this.$scope.observer.emit('Error', e));
        }
        observeProposals() {
            this.listen('proposal', response => {
                const { passthrough, proposal } = response;
                if(!this.data.forgetProposalIds.includes(proposal.id)) {
                    if (
                        this.data.proposals.findIndex(p => p.id === proposal.id) === -1
                    ) {
                        this.data.proposals.push({ ...proposal, ...passthrough });
                        this.data.proposalsLive.push({ ...proposal, ...passthrough });
                        this.checkProposalReady();
                    } else if (
                        this.data.proposalsLive.findIndex(p => p.id === proposal.id) >= 0
                    ) {
                        const index = this.data.proposalsLive.findIndex(p => p.id === proposal.id);
                        const obj = {...proposal, ...passthrough};
                        if(this.data.proposalsLive[index].contract_details.ticks_stayed_in) {
                            const lastCount = obj.contract_details.ticks_stayed_in.slice(-1)[0];
                            obj.contract_details.ticks_stayed_in = this.data.proposalsLive[index].contract_details.ticks_stayed_in;
                            if(lastCount > 0) {
                                obj.contract_details.ticks_stayed_in[obj.contract_details.ticks_stayed_in.length - 1] = lastCount;
                            } else {
                                obj.contract_details.ticks_stayed_in.push(lastCount);
                            }
                        }

                        this.data.proposalsLive[index] = obj;
                    }
                }
            });
            this.listenVirtual('proposal', response => {
                const { passthrough, proposal } = response;
                if(!this.dataVirtual.forgetProposalIds.includes(proposal.id)) {
                    if (
                        this.dataVirtual.proposals.findIndex(p => p.id === proposal.id) === -1
                    ) {
                        this.dataVirtual.proposals.push({ ...proposal, ...passthrough });
                        this.dataVirtual.proposalsLive.push({ ...proposal, ...passthrough });
                        this.checkProposalReady();
                    } else if (
                        this.dataVirtual.proposalsLive.findIndex(p => p.id === proposal.id) >= 0
                    ) {
                        const index = this.dataVirtual.proposalsLive.findIndex(p => p.id === proposal.id);
                        const obj = {...proposal, ...passthrough};
                        if(this.dataVirtual.proposalsLive[index].contract_details.ticks_stayed_in) {
                            const lastCount = obj.contract_details.ticks_stayed_in.slice(-1)[0];
                            obj.contract_details.ticks_stayed_in = this.dataVirtual.proposalsLive[index].contract_details.ticks_stayed_in;
                            if(lastCount > 0) {
                                obj.contract_details.ticks_stayed_in[obj.contract_details.ticks_stayed_in.length - 1] = lastCount;
                            } else {
                                obj.contract_details.ticks_stayed_in.push(lastCount);
                            }
                        }
                        this.dataVirtual.proposalsLive[index] = obj;
                    }
                }
            });
        }
        unsubscribeProposals() {
            const { proposals: prop} = this.dataVirtual;
            let removeForgetProposalById = forgetProposalId => {
                this.dataVirtual.forgetProposalIds = this.dataVirtual.forgetProposalIds.filter(id => id !== forgetProposalId);
            };

            this.clearProposalsVirtual();

            Promise.all(
                prop.map(proposal => {
                    if (!this.dataVirtual.forgetProposalIds.includes(proposal.id)) {
                        this.dataVirtual.forgetProposalIds.push(proposal.id);
                    }

                    if (proposal.error) {
                        removeForgetProposalById(proposal.id);
                        return Promise.resolve();
                    }
                    return doUntilDone(() => this.virtualApi.unsubscribeByID(proposal.id)).then(() =>
                        removeForgetProposalById(proposal.id)
                    );
                })
            );


            const { proposals } = this.data;
            removeForgetProposalById = forgetProposalId => {
                this.data.forgetProposalIds = this.data.forgetProposalIds.filter(id => id !== forgetProposalId);
            };

            this.clearProposals();

            return Promise.all(
                proposals.map(proposal => {
                    if (!this.data.forgetProposalIds.includes(proposal.id)) {
                        this.data.forgetProposalIds.push(proposal.id);
                    }

                    if (proposal.error) {
                        removeForgetProposalById(proposal.id);
                        return Promise.resolve();
                    }
                    return doUntilDone(() => this.api.unsubscribeByID(proposal.id)).then(() =>
                        removeForgetProposalById(proposal.id)
                    );
                })
            );
        }
        async checkProposalReady() {
            const { proposals } = this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing ? this.dataVirtual : this.data;
            if (proposals.length > 0) {
                const hasEqualProposals = this.proposalTemplates.every(
                    template =>
                        proposals.findIndex(
                            proposal => proposal.purchaseReference === template.passthrough.purchaseReference &&
                                proposal.contractType === template.contract_type

                        ) !== -1
                );

                if (hasEqualProposals) {
                    // await Promise.all([
                    //     () => this.startVirtual,
                    //     () => this.startPromise,
                    // ]);
                    // await this.startPromise;
                    // this.store.dispatch(proposalsReady());
                    this.startPromise.then(() => this.store.dispatch(proposalsReady()));
                }
            }
        }
        isNewTradeOption(tradeOption) {
            if (!this.tradeOption) {
                this.tradeOption = tradeOption;
                return true;
            }

            // Compare incoming "tradeOption" argument with "this.tradeOption", if any
            // of the values is different, this is a new tradeOption and new proposals
            // should be generated.
            const fieldsToCheck = [
                'amount',
                'barrierOffset',
                'basis',
                'duration',
                'duration_unit',
                'prediction',
                'secondBarrierOffset',
                'growthRate',
                'multiplier',
                // 'limit_order',
                'symbol',
            ];

            const changedFields = [];

            fieldsToCheck.forEach(field => {
                if (this.tradeOption[field] !== tradeOption[field]) {
                    changedFields.push({
                        field,
                        oldValue: this.tradeOption[field],
                        newValue: tradeOption[field],
                    });
                }
            });

            if (changedFields.length > 0) {
                // console.log('Trade option changed:', changedFields);
                return true;
            }

            return false;
        }
    };



// WEBPACK FOOTER //
// ./src/botPage/bot/TradeEngine/Proposal.js